/**
 * Repartir unidades entre los colores del modelo.
 *
 * DE DONDE SALE
 * -------------
 * Un sellado no tiene un color: el modelo viene en los que Apple hizo, y las
 * unidades que hay en el local sí son de un color cada una. Esa distinción ya
 * la tenía la sección, pero las pantallas que suman stock preguntaban el color
 * de a uno: «¿de qué color son?», un chip, y listo. Con una unidad alcanza. Con
 * tres no, y lo que pasaba era que la pregunta se saltaba entera y las unidades
 * quedaban colgadas de la fila del modelo, sin color — que es justo lo que
 * después nadie puede contestar por teléfono.
 *
 * Acá la pregunta cambia de forma: no es «de qué color son» sino «cuántas de
 * cada uno», y el total tiene que cerrar. Es la misma operación que hace
 * alguien parado frente al estante con tres cajas en la mano.
 *
 * POR QUE UN CONTADOR Y NO UN CAMPO DE TEXTO
 * ------------------------------------------
 * Los números son chicos —dos, tres, cinco— y esto se usa con el teléfono en
 * la mano, contando cajas. Tocar «+» una vez por caja es la misma cantidad de
 * gestos que hacer el conteo, y no abre el teclado numérico ni obliga a leer
 * «2,00» donde lo que hay son dos equipos.
 *
 * NO SABE DE PRODUCTOS NI DE MOVIMIENTOS. Recibe un lineup y un total, y
 * devuelve cuántas de cada color. Quién busca la ficha, quién la crea y qué
 * movimiento se escribe es problema de la pantalla.
 */
import { Minus, Plus } from "lucide-react";

import { IconButton } from "@/components/ui/icon-button";
import { cn } from "@/lib/utils";

/** Cuántas unidades van a cada color. Solo los que tienen más de cero. */
export interface UnidadesDeColor {
  /** El color como lo guarda la ficha: "BLACK", "BURGUNDY". */
  color: string;
  cantidad: number;
}

/** Cuántas se repartieron hasta ahora. */
export function repartidas(reparto: ReadonlyArray<UnidadesDeColor>): number {
  return reparto.reduce((t, r) => t + r.cantidad, 0);
}

/**
 * Lo que falta para poder confirmar, en palabras, o `null` si ya cierra.
 *
 * Es el texto que se muestra Y la condición que habilita el botón: una sola
 * definición, para que no pueda pasar que la pantalla diga «faltan 2» y el
 * botón esté prendido igual.
 */
export function faltaParaRepartir(
  reparto: ReadonlyArray<UnidadesDeColor>,
  total: number,
): string | null {
  const puestas = repartidas(reparto);
  if (puestas === total) return null;
  if (puestas > total) {
    const sobran = puestas - total;
    return sobran === 1 ? "Pusiste una de más." : `Pusiste ${sobran} de más.`;
  }
  const faltan = total - puestas;
  return faltan === 1 ? "Falta una." : `Faltan ${faltan}.`;
}

/** Lo que se manda: los colores en cero no son un movimiento de nada. */
export function soloLasQueVan(
  reparto: ReadonlyArray<UnidadesDeColor>,
): ReadonlyArray<UnidadesDeColor> {
  return reparto.filter((r) => r.cantidad > 0);
}

export interface RepartoPorColorProps {
  /** El lineup del modelo: en qué colores viene. Sale de `coloresDelModelo`. */
  colores: ReadonlyArray<{ etiqueta: string; hex: string }>;
  /** Cuántas unidades hay que repartir. */
  total: number;
  reparto: ReadonlyArray<UnidadesDeColor>;
  onRepartoChange: (reparto: ReadonlyArray<UnidadesDeColor>) => void;
}

export function RepartoPorColor({
  colores,
  total,
  reparto,
  onRepartoChange,
}: RepartoPorColorProps): JSX.Element {
  const falta = faltaParaRepartir(reparto, total);
  const puestas = repartidas(reparto);

  function cantidadDe(color: string): number {
    return reparto.find((r) => r.color === color)?.cantidad ?? 0;
  }

  function mover(color: string, delta: number): void {
    const actual = cantidadDe(color);
    const nueva = Math.max(actual + delta, 0);
    const sinEse = reparto.filter((r) => r.color !== color);
    onRepartoChange(nueva === 0 ? sinEse : [...sinEse, { color, cantidad: nueva }]);
  }

  return (
    <div className="flex flex-col gap-2">
      <ul className="flex flex-col gap-1">
        {colores.map((c) => {
          const cantidad = cantidadDe(c.etiqueta.toUpperCase());
          return (
            <li key={c.etiqueta} className="flex items-center gap-3">
              <span
                aria-hidden="true"
                className="size-4 shrink-0 rounded-sm border border-border"
                style={{ backgroundColor: c.hex }}
              />
              <span className="flex-1 text-sm">{c.etiqueta}</span>
              <IconButton
                icono={Minus}
                tamano="sm"
                variant="outline"
                aria-label={`Una menos de ${c.etiqueta}`}
                disabled={cantidad === 0}
                onClick={() => mover(c.etiqueta.toUpperCase(), -1)}
              />
              {/* Tabular para que la columna no se mueva al pasar de 9 a 10. */}
              <span
                className={cn(
                  "w-6 text-center text-sm tabular-nums",
                  cantidad === 0 && "text-muted-foreground",
                )}
              >
                {cantidad}
              </span>
              <IconButton
                icono={Plus}
                tamano="sm"
                variant="outline"
                aria-label={`Una más de ${c.etiqueta}`}
                onClick={() => mover(c.etiqueta.toUpperCase(), 1)}
              />
            </li>
          );
        })}
      </ul>

      {/*
        El contador se lee en voz alta porque es la unica senal de que falta
        algo: quien no ve la pantalla toca «+» y no se entera de que ya cerro.
      */}
      <p
        aria-live="polite"
        className={cn("text-sm", falta ? "text-muted-foreground" : "text-foreground")}
      >
        Repartidas {puestas} de {total}
        {falta ? ` · ${falta}` : " · listo"}
      </p>
    </div>
  );
}
